<template>
  <modal
    v-if="show"
    bodyPadding="0"
    :width="width"
    class="details-dialog"
    :title="modalTitle"
    @close="close"
    bodyOverflow="auto"
    :actionBeforeLogout="actionBeforeLogout"
    :closeBtn="isAllowCloseButton"
    :logOutBtn="!isAllowCloseButton"
  >
    <div :class="contentClass" v-if="mode === Modes.planDetails">
      <ChangePlanDetails
        :handlePlanSelect="handlePlanSelect"
        :planLimits="data.planLimits"
        :billingPlans="plansList"
        :billing="subscriptionInfo"
        @select-plan="handlePlanSelect"
        @close="close"
      />
    </div>
    <div :class="contentClass" v-if="mode === Modes.step1">
      <div class="list">
        <ChangePlanItem
          v-for="item in plansList"
          :plan="item"
          :selectedPlanId="selected.id"
          :handlePlanSelect="handlePlanSelect"
        />
      </div>
    </div>

    <div :class="contentClass" v-if="mode === Modes.step2">
      <change-plan_step2
        :estimate="estimate"
        :selected="selected"
        :currencyFormat="currencyFormat"
      />
    </div>

    <div :class="contentClass" v-if="mode === Modes.cantDowngrade">
      <forbidden-to-change-plan :plan="selected" />
    </div>

    <template #footer>
      <section class="footer xs12" v-if="mode === Modes.cantDowngrade">
        <ui-button
          :text="t('Back')"
          icon="fas fa-arrow-left"
          :disabled="loading"
          @click="handleBack"
        />
      </section>
      <section
        class="footer xs12"
        v-if="mode === Modes.step1 || mode === Modes.planDetails"
      >
        <ui-button
          v-if="!data.registration"
          :text="t('Cancel')"
          icon="fas fa-times"
          :disabled="loading"
          @click="close"
        />
        <ui-button
          :text="t('Compare All Plans')"
          icon="fas fa-list"
          @click="compareHandle"
        />
        <ui-button
          v-if="showBuyAsAddon"
          :text="t('Buy as Add-On')"
          icon="fas fa-badge-dollar"
          @click="byAsAddonHandle"
          button-style="blue"
        />
        <ui-button
          v-if="selected.id"
          :disabled="isContinueDisabled"
          icon="fas fa-check"
          :text="t('Continue With {0} Plan', [selected && selected.name])"
          button-style="accent"
          @click="handlePlanSelectConfirm"
        />
      </section>

      <section class="footer xs12" v-if="mode === Modes.step2">
        <ui-button
          :text="t('Back')"
          icon="fas fa-arrow-left"
          v-if="!data.preSelectedPlan"
          :disabled="loading"
          @click="handleBack"
        />
        <ui-button
          :text="t('Cancel')"
          icon="fas fa-times"
          v-if="data.preSelectedPlan"
          @click="close"
        />
        <ui-button
          :text="
            selected.id === 'basic' || selected.id === 'free'
              ? t('Confirm Action')
              : t('Confirm Payment')
          "
          icon="fas fa-check"
          button-style="accent"
          :loading="loading"
          :disabled="estimate?.estimateType === 'lessThan50cents'"
          @click="submit"
        />
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  getBillingCustomerApiRequest,
  getBillingPlansApiRequest,
  getBillingSubscriptionApiRequest,
  postUpdateBillingSubscriptionApiRequest,
  postUpdateBillingSubscriptionEstimatedApiRequest,
} from '@/services/api/billing-api';
import { AppErrorEnum } from '@/services/models/Enums';
import { getUserApiRequest } from '@/services/api/user-api';
import ChangePlan_step2 from './change-plan_step2.vue';
import ForbiddenToChangePlan from './forbidden-to-change-plan.vue';
import ChangePlanItem from './change-plan-item.vue';
import ChangePlanDetails from './change-plan-details.vue';
import type { PlanLimitsProp } from '@/composables';
import _ from 'lodash';
import { availablePlanLimits, type BillingPlan } from '@/services/models';
import type { BillingSubscriptionState } from '@/services/models';
import { useI18n } from '@/composables';
import { $emitter } from '@/services/emitter';
import { useStore } from '@/store';

enum Modes {
  step1 = 'step1',
  step2 = 'step2',
  planDetails = 'planDetails',
  cantDowngrade = 'cantDowngrade',
}

export interface Data {
  dontClose: boolean;
  registration: boolean;
  actionAfterLoad: Function | null;
  preSelectedPlan?: BillingPlan;
  planLimits: PlanLimitsProp | null;
}

const props = withDefaults(
  defineProps<{
    data?: Data;
    format?: string;
  }>(),
  {
    format: 'DD MMMM YYYY HH:mm',
    data: () => ({
      dontClose: false,
      registration: false,
      planLimits: null,
      actionAfterLoad: null,
    }),
  },
);

const emit = defineEmits<{
  close: [];
}>();

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const show = ref(false);
const mode = ref<Modes | null>(null);
const planId = ref('');
const planIdRef = planId;
const selected = ref<BillingPlan>({} as BillingPlan);
const interval = ref<ReturnType<typeof setInterval> | null>(null);
const estimate = ref<any>(null);
const currencyFormat = ref('0,0.00');
const loading = ref(false);

const plansList = computed<BillingPlan[]>(() => store.billingPlans ?? []);
const subscriptionInfo = computed<BillingSubscriptionState>(() => store.billing);

const isAllowCloseButton = computed(
  () =>
    !(store.appError === AppErrorEnum.freePlanLimitsExceeded || props.data.dontClose),
);

const isContinueDisabled = computed(
  () =>
    loading.value ||
    ((store.appError === AppErrorEnum.freePlanLimitsExceeded ||
      props.data.dontClose) &&
      (selected.value.id === 'basic' || selected.value.id === 'free')),
);

const modalTitle = computed(() => {
  if (props.data.registration) return t('Select Pricing Plan');
  if (props.data.planLimits) return t('Upgrade Subscription');
  return t('Change Plan');
});

// String limits (e.g. landingsAmount) and plain keys show Buy as Add-On.
// Grouped limits with `logic` use per-addon buttons in the details body instead.
const showBuyAsAddon = computed(() => {
  if (mode.value !== Modes.planDetails) return false;
  const limits = props.data.planLimits;
  if (!limits) return false;
  if (typeof limits === 'object' && 'logic' in limits && limits.logic) return false;
  return true;
});

const contentClass = computed(() =>
  ['change-plan-dialog', `change-plan-dialog--${mode.value}`].join(' '),
);

const width = computed(() => {
  switch (mode.value) {
    case Modes.cantDowngrade:
      return '600px';
    case Modes.planDetails:
      return '800px';
    case Modes.step1: {
      const w = 202 * Math.min(plansList.value.length, 5);
      return `${w}px`;
    }
    default:
      return '556px';
  }
});

const load = async () => {
  const [plans, subscription] = await Promise.all([
    getBillingPlansApiRequest(),
    getBillingSubscriptionApiRequest(),
  ]);
  store.setBillingPlans(plans);
  store.setBilling(subscription);
};

const openUpdatePaymentDialog = () => {
  $emitter.emit('open-dialog', { name: 'UpdatePaymentMethodDialog' });
};

const updateSubscription = async (val: string) => {
  store.setLoading(true);
  loading.value = true;
  try {
    await postUpdateBillingSubscriptionApiRequest({ planId: val }, true);
    if (
      store.user?.blockedCampaigns ||
      store.appError === AppErrorEnum.freePlanLimitsExceeded
    ) {
      interval.value = setInterval(async () => {
        const userData = await getUserApiRequest();
        if (userData?.pendingInvoiceGeneration === false) {
          if (interval.value) clearInterval(interval.value);
          window.location.reload();
        }
      }, 3000);
    } else {
      const [subscription, customer] = await Promise.all([
        getBillingSubscriptionApiRequest(),
        getBillingCustomerApiRequest(),
      ]);
      store.setBilling(subscription);
      store.setBillingCustomer(customer);
      close();
      $emitter.emit('toast-success', t('Subscription plan successfully changed.'));
    }
  } catch (err: any) {
    if (err?.response?.status === 500) {
      $emitter.emit(
        'toast-error',
        t('Server Overload: Subscription change failed. Please try again later.'),
      );
    } else if (err?.response?.data?.errors) {
      const errors = Object.values(err.response.data.errors);
      for (const error of errors) {
        if (error === 'VALIDATION_NO_CARD_PROVIDED') {
          openUpdatePaymentDialog();
        } else {
          $emitter.emit('toast-error', t(String(error)));
        }
      }
    }
  } finally {
    store.setLoading(false);
    loading.value = false;
  }
};

const toPlan = async (planId: string) => {
  planIdRef.value = planId;
  store.setLoading(true);
  loading.value = true;
  try {
    const data: any = await postUpdateBillingSubscriptionEstimatedApiRequest(
      { planId },
      true,
    );
    if (
      !data?.next_invoice_estimate &&
      !data?.invoice_estimate &&
      planId !== 'basic' &&
      planId !== 'free'
    ) {
      await updateSubscription(planId);
    } else {
      estimate.value = data;
      mode.value = Modes.step2;
      show.value = true;
    }
  } catch (err: any) {
    if (err?.response?.status === 500) {
      $emitter.emit(
        'toast-error',
        t('Server Overload: Subscription change failed. Please try again later.'),
      );
      return;
    }
    if (err?.response?.data?.message === 'SUBSCRIPTION_UPDATE_FORBIDEN') {
      const { limits, usages } = err.response.data.payload;
      store.setBilling({
        ...subscriptionInfo.value,
        limits: _.pick(limits, availablePlanLimits),
        usage: _.pick(usages, availablePlanLimits),
      } as BillingSubscriptionState);
      await handlePlanSelectConfirm();
    }
    if (err?.response?.data?.message === 'SUBSCRIPTION_UPDATE_TOTAL_LESS_THAN_50_CENTS') {
      estimate.value = {
        ...err.response.data.payload,
        estimateType: 'lessThan50cents',
      };
      mode.value = Modes.step2;
      show.value = true;
    }
  } finally {
    store.setLoading(false);
    loading.value = false;
  }
};

const close = () => {
  if (isAllowCloseButton.value) {
    emit('close');
    $emitter.emit('close-all-dialogs');
    if (interval.value) clearInterval(interval.value);
  }
};

const compareHandle = () => {
  router.push('/app/profile/plan-description');
  $emitter.emit('close-all-dialogs');
};

const byAsAddonHandle = () => {
  emit('close');
  $emitter.emit('close-all-dialogs');
  $emitter.emit('open-dialog', {
    name: 'ManageAddon',
    data: { planLimit: props.data.planLimits },
  });
};

const actionBeforeLogout = () => {
  emit('close');
  $emitter.emit('close-all-dialogs');
  if (interval.value) clearInterval(interval.value);
};

const handlePlanSelect = (plan: BillingPlan) => {
  selected.value = { ...plan };
};

const isForbiddenToChangePlan = (): boolean => {
  const usage = subscriptionInfo.value.usage ?? {};
  const addons = subscriptionInfo.value.addons ?? {};
  const newPlanLimits = selected.value.limits;
  if (!newPlanLimits) return false;
  return Object.keys(usage).some(key => {
    const addonLimits = +addons?.[key]?.addonLimit || 0;
    return +usage[key] > +newPlanLimits[key] + addonLimits;
  });
};

const handlePlanSelectConfirm = async () => {
  const currentPlanId = subscriptionInfo.value?.planId;
  if (!selected.value?.id) {
    return;
  }
  // Same plan: only close when browsing all plans. In upgrade flow the
  // recommended plan must differ — closing here was a no-op upgrade.
  if (currentPlanId === selected.value.id) {
    if (mode.value === Modes.planDetails) {
      return;
    }
    close();
    return;
  }
  if (isForbiddenToChangePlan()) {
    mode.value = Modes.cantDowngrade;
    return;
  }
  await toPlan(selected.value.id);
};

const handleBack = () => {
  mode.value = Modes.step1;
};

const submit = () => {
  return updateSubscription(planId.value);
};

store.setLoading(true);
load()
  .then(() => {
    if (props.data.preSelectedPlan) {
      handlePlanSelect(props.data.preSelectedPlan);
      handlePlanSelectConfirm().then(() => {
        show.value = true;
        props.data.actionAfterLoad?.();
      });
    } else {
      mode.value = props.data.planLimits ? Modes.planDetails : Modes.step1;
      if (props.data.planLimits) {
        // Leave empty until ChangePlanDetails selects the upgrade plan
        selected.value = {} as BillingPlan;
      } else {
        const currentPlan = plansList.value.find(
          plan => plan.id === subscriptionInfo.value?.planId,
        );
        selected.value =
          currentPlan ??
          ({
            id: subscriptionInfo.value?.planId,
            name: subscriptionInfo.value?.planName,
            limits: subscriptionInfo.value?.limits,
            price: subscriptionInfo.value?.planPrice,
          } as BillingPlan);
      }
      show.value = true;
      props.data.actionAfterLoad?.();
    }
  })
  .finally(() => {
    store.setLoading(false);
  });
</script>

<style lang="scss" scoped>
.footer {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  row-gap: 10px;
}
.list {
  display: flex;
  padding: 8px;
  .item {
    margin: 8px;
  }
}
</style>

<style lang="scss">
.change-plan-dialog {
  &--planDetails,
  &--step1 {
    height: 100%;
    max-height: 510px !important;
    width: 100%;
    display: inline-block;
    .v-card {
      height: 100%;
      max-height: 510px;
    }
  }

  @media (max-width: 800px) {
    & {
      height: 100%;
      max-height: 900px !important;
    }
  }

  @media (max-width: 400px) {
    &__plan-list-wrapper {
      width: 100% !important;
    }
  }

  .v-card__text {
    padding: 0;
  }

  &__esitmate-header {
    font-size: 20px;
    color: var(--color-graphite);

    & + & {
      margin-left: 4px;
    }

    &--caption {
      font-weight: bold;
    }

    &--name {
      font-weight: normal;
    }
  }

  &__esitmate-subtitle {
    font-size: 14px;
  }

  &__esitmate-list {
    &--border {
      border-bottom: 1px solid var(--color-gray10);
    }

    .v-list__tile {
      padding: 8px 0;
      height: unset;
    }
  }

  &__esitmate-content {
    font-size: 14px;

    &--money {
      flex-grow: 0;
    }

    &--total {
      font-weight: bold;
    }
  }
}
</style>
